/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
 *      1       | 11/03/2025 | Lic. José Luis Aragón Cervantes  | Creación
 *      2       | 15/04/2026 |                               | Navegación mejorada con recarga servidor
 *      3       | 08/10/2026 | LIC. José Luis Aragón C.                       | Cabecero primary (th + header igual
 *      4       | 10/10/2026 | Lic. José Luis Aragón C.                       | Push-to-talk: el micrófono se escucha mientras se mantiene presionado y entrega el texto al soltar
 *              |            |                                  | que demás buscadores) + scroll
 *              |            |                                  | relativo al contenedor (el avance
 *              |            |                                  | se perdía por offsetParent fijo)
 * --------------------------------------------------------------------------------------- 
 */

import TextField from "../ui/TextField";
import React, { useState, useEffect, useRef } from "react";
import { Modal, Box, IconButton, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { copiarTexto } from "../../helpers/portapapeles";
import { apiBuscararticulosFiltroFull } from "../../settings/ApiConfig";
import { useLayout } from "../../context/System/LayoutContext";
import { useRequest } from "../../context/System/RequestContext";
import { useArticulos } from "../../context/Catalogos/ArticulosContext";
import useSpeechToText from "../leyout/useSpeechToText";
import BotonVoz from "../leyout/BotonVoz";

const modalStyle = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '75vw',
    maxWidth: '900px',
    height: '90vh',
    maxHeight: '720px',
    bgcolor: 'background.paper',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    borderRadius: '8px',
    p: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
};

export default function ModalBuscarArticulosSimple({
    openModal,
    handleCloseModal,
    handleArticuloSelect
}) {

    const { GetRequest } = useRequest();
    const { handleOpenAlert } = useLayout();
    const { handleObtenerArticuloByClave } = useArticulos();

    const { isSupported, hasPermission, isListening, error: speechError, startListening, stopListening, checkPermission } = useSpeechToText({
        onResult: (text) => {
            const cleaned = text.replace(/[.]+$/, '').trim();
            setFiltro(cleaned);
            buscar();
            setTimeout(() => inputRef.current?.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true })), 100);
        }
    });

    const inputRef = useRef(null);
    const tablaRef = useRef(null);

    // 🔥 Persistencia
    const persistRef = useRef({
        filtro: "",
        lista: [],
        indexSel: -1
    });

    const [filtro, setFiltro] = useState(persistRef.current.filtro);
    const [lista, setLista] = useState(persistRef.current.lista);
    const [indexSel, setIndexSel] = useState(persistRef.current.indexSel);

    // guardar estado
    useEffect(() => {
        persistRef.current = { filtro, lista, indexSel };
    }, [filtro, lista, indexSel]);

    // apertura
    useEffect(() => {
        if (openModal) {
            setFiltro(persistRef.current.filtro);
            setLista(persistRef.current.lista);
            setIndexSel(persistRef.current.indexSel);

            requestAnimationFrame(() => {
                if (persistRef.current.indexSel >= 0) {
                    tablaRef.current?.focus();
                    scrollToIndex(persistRef.current.indexSel);
                } else {
                    inputRef.current?.focus();
                    inputRef.current?.select();
                }
            });
        }
    }, [openModal]);

    useEffect(() => {
        if (openModal) checkPermission();
    }, [openModal, checkPermission]);

    // ⌨️ Manejo de teclado global cuando el modal esté abierto
    useEffect(() => {
        if (!openModal) return;

        const handleKeyDownGlobal = (event) => {
            if (event.altKey && event.key.trim().toUpperCase() === "F1") {
                event.preventDefault();
                event.stopPropagation();
                
                inputRef.current?.focus();
                inputRef.current?.select();
            }
        };

        window.addEventListener("keydown", handleKeyDownGlobal);
        return () => window.removeEventListener("keydown", handleKeyDownGlobal);
    }, [openModal]);

    // 🔎 buscar
    const buscar = async () => {
        if (!filtro) return;

        try {
            let url = apiBuscararticulosFiltroFull
                .replace("{busqueda}", encodeURIComponent(filtro))
                .replace("{registroinicial}", 0)
                .replace("{totalregistros}", 1000);

            const res = await GetRequest({
                url,
                loader: "Buscando artículos"
            });

            if (!res || res.hasError) {
                handleOpenAlert(res?.message || "Error al buscar artículos");
                return;
            }

            if (!res.result) {
                setLista([]);
                return;
            }

            const data = res.result.listaarticulos || [];

            setLista(data);

            if (data.length > 0) {
                setIndexSel(0);

                setTimeout(() => {
                    tablaRef.current?.focus();
                    scrollToIndex(0);
                }, 50);
            }
        } catch (error) {
            handleOpenAlert("Error al buscar: " + (error?.message || error));
        }
    };

    // ðŸ” scroll correcto
    const scrollToIndex = (index) => {
        const container = tablaRef.current;
        const rows = container?.querySelectorAll(".row-articulo");

        if (!rows || !rows[index]) return;

        const row = rows[index];
        const rowTop = row.offsetTop;
        const rowBottom = rowTop + row.offsetHeight;

        const viewTop = container.scrollTop;
        const viewBottom = viewTop + container.clientHeight;

        if (rowBottom > viewBottom) {
            container.scrollTop = rowBottom - container.clientHeight;
        }

        if (rowTop < viewTop) {
            container.scrollTop = rowTop;
        }
    };

    // ⌨️ INPUT
    const handleKeyDownInput = async (e) => {

        if (e.key === "Enter") {
            e.preventDefault();
            await buscar();
        }

        if (e.key === "ArrowDown") {
            if (lista.length > 0) {
                e.preventDefault();

                setIndexSel(0);

                setTimeout(() => {
                    tablaRef.current?.focus();
                    scrollToIndex(0);
                }, 10);
            }
        }
    };

    // ⌨️ TABLA
    const handleKeyDownTabla = async (e) => {
        if (!lista.length) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();

            const next = indexSel + 1;

            if (next < lista.length) {
                setIndexSel(next);
                scrollToIndex(next);
            }
        }

        if (e.key === "ArrowUp") {
            e.preventDefault();

            if (indexSel > 0) {
                const prev = indexSel - 1;
                setIndexSel(prev);
                scrollToIndex(prev);
            } else {
                // 🔥 liberar foco de la tabla
                tablaRef.current?.blur();

                // 🔥 enfocar input REAL
                inputRef.current?.focus();
                inputRef.current?.select();

                return;
            }
        }

        if (e.key === "Enter") {
            const art = lista[indexSel];
            if (!art) return;

            const data = await handleObtenerArticuloByClave(art.clavearticulo, false);
            if (!data) return;

            await handleArticuloSelect(data);
            handleCloseModal();
        }

        if (e.key === "Escape") {
            handleCloseModal();
        }
    };

    const handleDoubleClickRow = async (art) => {
        const data = await handleObtenerArticuloByClave(art.clavearticulo, false);
        if (!data) return;
        await handleArticuloSelect(data);
        handleCloseModal();
    };

    const obtenerCodigoCopiable = (a) =>
        (a?.codigobarras ?? a?.codigoBarras ?? a?.sCodigoBarras ?? a?.clavearticulo ?? "");

    const colEstilos = {
        descripcion: { width: "35%", textAlign: "left" },
        material: { width: "15%", textAlign: "left" },
        fabricante: { width: "15%", textAlign: "left" },
        familia: { width: "15%", textAlign: "left" },
        clave: { width: "20%", textAlign: "left" }
    };

    const thStyle = {
        padding: "8px",
        background: "#0062AE", // primary.main (igual que demás buscadores)
        color: "#fff",
        borderBottom: "2px solid #0062AE",
        fontWeight: "bold",
        fontSize: "13px"
    };

    const headerStyle = {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        backgroundColor: "#0062AE", // primary.main (igual que demás buscadores)
        color: "#fff",
        borderRadius: "8px 8px 0 0"
    };

    const tdStyle = {
        padding: "8px",
        borderBottom: "1px solid #eee"
    };

    return (
        <Modal open={openModal} onClose={handleCloseModal}>
            <Box sx={modalStyle}>
                {/* HEADER */}
                <div style={headerStyle}>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                        Buscar Artículo
                    </Typography>
                    <IconButton onClick={handleCloseModal} sx={{ color: '#fff' }}>
                        <CloseIcon />
                    </IconButton>
                </div>

                {/* FILTRO */}
                <div style={{ padding: '16px', borderBottom: '1px solid #eee' }}>
                    <TextField
                        inputRef={inputRef}
                        label="Filtro (Alt+F1 para enfocar)"
                        value={filtro}
                        onChange={(e) => setFiltro(e.target.value)}
                        onKeyDown={handleKeyDownInput}
                        autoComplete="off"
                        size="small"
                        fullWidth
                        inputMode="text"
                        slotProps={{ htmlInput: { enterKeyHint: "enter" } }}
                        sx={{ maxWidth: 400 }}
                    />
                    <BotonVoz
                            isSupported={isSupported}
                            hasPermission={hasPermission}
                            isListening={isListening}
                            onStart={startListening} onStop={stopListening}
                        />
                </div>

                <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", padding: '0 8px' }}>
                    {/* HEADER */}
                    <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <th style={{ ...thStyle, ...colEstilos.descripcion }}>Descripción</th>
                                <th style={{ ...thStyle, ...colEstilos.material }}>Material</th>
                                <th style={{ ...thStyle, ...colEstilos.fabricante }}>Fabricante</th>
                                <th style={{ ...thStyle, ...colEstilos.familia }}>Familia</th>
                                <th style={{ ...thStyle, ...colEstilos.clave }}>Clave</th>
                            </tr>
                        </thead>
                    </table>

                    {/* BODY */}
                    <div
                        ref={tablaRef}
                        tabIndex={0}
                        onKeyDown={handleKeyDownTabla}
                        // Rev 3: position relative para que offsetTop de los
                        // renglones se mida contra este contenedor (su
                        // offsetParent). Sin esto el scroll se calculaba contra
                        // el Box fijo y el avance se perdía de vista.
                        style={{ overflowY: "auto", flex: 1, position: "relative", outline: "none" }}
                    >
                        <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse" }}>
                            <tbody>
                                {lista.length > 0 ? (
                                    lista.map((a, i) => (
                                        <tr
                                            key={i}
                                            className="row-articulo"
                                            onClick={() => setIndexSel(i)}
                                            onDoubleClick={() => handleDoubleClickRow(a)}
                                            style={{
                                                background: i === indexSel ? "#e3f2fd" : "white",
                                                cursor: "pointer"
                                            }}
                                        >
                                            <td style={{ ...tdStyle, ...colEstilos.descripcion }}>{a.descripcion}</td>
                                            <td style={{ ...tdStyle, ...colEstilos.material }}>{a.material}</td>
                                            <td style={{ ...tdStyle, ...colEstilos.fabricante }}>{a.fabricante}</td>
                                            <td style={{ ...tdStyle, ...colEstilos.familia }}>{a.familia}</td>
                                            <td style={{ ...tdStyle, ...colEstilos.clave }}>{a.clavearticulo}
                                                <IconButton size="small" title="Copiar código de barras" aria-label="Copiar código de barras" sx={{ ml: 0.5, p: 0.25 }} onClick={(e) => { e.stopPropagation(); copiarTexto(obtenerCodigoCopiable(a)); }}>
                                                    <ContentCopy fontSize="inherit" sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={5} style={{ textAlign: "center", padding: 20 }}>
                                            {filtro ? "Sin resultados" : "Escribe y presiona Enter"}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Box>
        </Modal>
    );
}
